import {FC, useEffect, useState} from 'react'
import {getAuth, removeAuth} from '../../../app/modules/auth/core/AuthHelpers'
import {Toast} from '../widgets'

interface JWTPayload {
  sub: number
  is_impersonating?: boolean
  impersonator_id?: number
  impersonator_token?: string
  [key: string]: any
}

// Utility function to decode JWT token
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }
    
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

export const ImpersonationBanner: FC = () => {
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [impersonatedUserName, setImpersonatedUserName] = useState('')

  useEffect(() => {
    const auth = getAuth()
    if (auth?.api_token) {
      const payload = decodeJWT(auth.api_token)
      if (payload?.is_impersonating) {
        setIsImpersonating(true)
        // We'll get the user name from the current user context
        // For now, just indicate impersonation is active
      }
    }
  }, [])

  const handleStopImpersonation = () => {
    Toast.fire({
      icon: 'success',
      title: 'Encerrando simulação',
      text: 'Redirecionando para o painel administrativo...',
    })
    
    // Clear the current auth (logout from impersonated user)
    removeAuth()
    
    // Remove the specific auth key from localStorage
    localStorage.removeItem('impersonating-auth-token')
    
    // Determine backend URL
    const currentHost = window.location.host
    let backendUrl = 'http://localhost:8000'
    
    if (currentHost.includes('hml.taskrush.com.br')) {
      backendUrl = 'https://admhml.taskrush.com.br'
    } else if (currentHost.includes('app.taskrush.com.br')) {
      backendUrl = 'https://adm.taskrush.com.br'
    }
    
    // Redirect to backend admin panel
    window.location.href = backendUrl + '/administrativo'
  }

  if (!isImpersonating) {
    return null
  }

  return (
    <div 
      className='bg-warning position-fixed top-0 start-0 end-0 d-flex align-items-center justify-content-between px-6 py-2 shadow-sm'
      style={{zIndex: 1000, height: '60px'}}
    >
      <div className='d-flex align-items-center'>
        <i className='bi bi-eye-fill fs-3 text-dark me-3'></i>
        <div>
          <span className='fw-bold text-dark fs-6'>
            Modo de Simulação Ativo
          </span>
          <p className='mb-0 text-dark small'>
            Você está visualizando o sistema como outro usuário
          </p>
        </div>
      </div>
      <button
        className='btn btn-dark btn-sm'
        onClick={handleStopImpersonation}
      >
        <i className='bi bi-x-circle me-2'></i>
        Parar Simulação
      </button>
    </div>
  )
}

// Hook to check if currently impersonating
export const useIsImpersonating = (): boolean => {
  const [isImpersonating, setIsImpersonating] = useState(false)

  useEffect(() => {
    const auth = getAuth()
    if (auth?.api_token) {
      const payload = decodeJWT(auth.api_token)
      setIsImpersonating(!!payload?.is_impersonating)
    }
  }, [])

  return isImpersonating
}

// Hook to get impersonation banner visibility and height
export const useImpersonationBannerVisible = () => {
  const [visible, setVisible] = useState(false)
  const height = 60 // Height of impersonation banner in pixels

  useEffect(() => {
    const auth = getAuth()
    if (auth?.api_token) {
      const payload = decodeJWT(auth.api_token)
      setVisible(!!payload?.is_impersonating)
    }
  }, [])

  return { visible, height }
}

// Constant for banner height
export const IMPERSONATION_BANNER_HEIGHT = 60

